import React, { useState, useEffect, useRef } from 'react';
import { X, Loader2, PauseCircle, Check } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { ExecutionModalTask } from './ExecutionModalTask';
import { ExecutionModalSpareParts } from './ExecutionModalSpareParts';
import { ExecutionModalAttachments } from './ExecutionModalAttachments';

// Tab navigation component matching ExecutionDetails style
const TabNav = ({ activeTab, setActiveTab }) => {
  const tabs = [
    { key: 'task', label: 'Task' },
    { key: 'spare_parts', label: 'Spare Parts' },
    { key: 'attachments', label: 'Attachments' },
  ];
  return (
    <div className="sticky top-0 z-10 px-6 border-b border-gray-700 bg-gray-800">
      <div className="flex gap-1">
        {tabs.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => setActiveTab(key)}
            className={`px-4 py-3 flex-1 text-sm font-medium transition-colors hover:bg-gray-750 cursor-pointer relative ${activeTab === key
              ? 'text-blue-400'
              : 'text-gray-400 hover:text-gray-200'
              }`}
          >
            {label}
            {activeTab === key && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export const ExecutionModal = ({ isOpen, onClose, onFinish, onUpdateTask, onUpdateAssig, onUpdateExtAssig, task, assig, workOrder, spareParts = [], documentTypes = [], }) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('');
  const [observations, setObservations] = useState('');
  const [completionFormData, setCompletionFormData] = useState({
    completion_notes: '',
    quality_check: null,
  });
  const [formErrors, setFormErrors] = useState('');

  const [localModalError, setLocalModalError] = useState(null);
  const [localIsSubmitting, setLocalIsSubmitting] = useState(false);

  const isCompleted = task?.status === 'completed';
  const pendingTasks = workOrder?.tasks?.filter(
    t => t.id !== task.id && t.status !== 'completed'
  );

  const isLastTask = pendingTasks.length === 0;
  const finishTrigger = isLastTask && task.task_number === workOrder.tasks.length && task.status === 'completed' && workOrder.status !== 'completed';


  const invalidateWorkOrders = () => {
    queryClient.invalidateQueries({ queryKey: ['wo'] });
  };

  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isPaused, setIsPaused] = useState(true);
  const [pauseMotive, setPauseMotive] = useState('');

  const formRef = useRef(null);

  // ── Initialize timer when modal opens ──────────────────────────────────────
  useEffect(() => {
    if (!isOpen || !task) return;
    const base = task.seconds_worked ?? 0;
    const startedAt = task.restart_at ? new Date(task.restart_at) : null;
    if (startedAt) {
      // Timer was running when we last saved — fast-forward to now
      const elapsed = base + Math.floor((Date.now() - startedAt.getTime()) / 1000);
      setElapsedSeconds(Math.max(0, elapsed));
      setIsPaused(false);
    } else {
      // Timer was paused — show frozen value
      setElapsedSeconds(base);
      setIsPaused(true);
    }
  }, [isOpen, task?.id]); // re-init only when the modal opens or a different task is loaded

  // ── Tick every second while running ────────────────────────────────────────
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => setElapsedSeconds(s => s + 1), 1000);
    return () => clearInterval(interval);
  }, [isPaused]);

  // ── Pause / Resume ─────────────────────────────────────────────────────────
  const [isTogglingPause, setIsTogglingPause] = useState(false);

  const togglePause = async () => {
    if (!onUpdateTask) return;
    setIsTogglingPause(true);
    try {
      if (isPaused) {
        // Resume: tell backend restart_at = now
        let new_pause_reasons = task.pause_reasons;

        if (pauseMotive.trim()) {
          const timestampStr = new Date().toLocaleString();
          const motiveStr = `\n[Paused at ${timestampStr}]\nMotive: ${pauseMotive.trim()}`;
          setPauseMotive('');
          if (new_pause_reasons === null) {
            new_pause_reasons = [motiveStr];
          } else {
            new_pause_reasons.push(motiveStr);
          }
          await onUpdateTask(task.id, { restart_at: new Date().toISOString(), pause_reasons: new_pause_reasons });
        }
        setIsPaused(false);
      } else {
        // Pause: save current seconds_worked and clear restart_at
        setIsPaused(true);
        // Incremental hours for this session = seconds worked since last save, converted to hours
        const sessionSeconds = elapsedSeconds - (task.seconds_worked ?? 0);
        await Promise.all([
          onUpdateTask(task.id, { seconds_worked: elapsedSeconds, restart_at: null }),
          !assig.company_id && onUpdateAssig
            ? onUpdateAssig(assig.id, { hours_worked: (assig.hours_worked ?? 0) + sessionSeconds / 3600 })
            : Promise.resolve(),
          assig.company_id != null && onUpdateExtAssig
            ? onUpdateExtAssig(assig.id, { hours_worked: (assig.hours_worked ?? 0) + sessionSeconds / 3600 })
            : Promise.resolve(),
        ]);

      }
    } catch (e) {
      console.error('Failed to toggle pause:', e);
    } finally {
      invalidateWorkOrders();
      setIsTogglingPause(false);
    }
  };

  // Format seconds to hh:mm:ss
  const formatTime = (totalSeconds) => {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };
  // Reset form fields when modal opens
  useEffect(() => {
    if (isOpen) {
      setLocalModalError(null);
      setFormErrors({});
      setActiveTab('task');
      setObservations(task.observations || '');
    }
  }, [isOpen]);

  const validateForm = () => {
    console.log('Validating...');
    const errors = {};

    if (!observations.trim()) {
      errors.observations = 'Observations are required';
    }
    setFormErrors(errors);
    console.log('Validation errors:', errors);

    return Object.keys(errors).length === 0;
  };

  const handleSaveDraft = async (e) => {

    setLocalIsSubmitting(true);
    setLocalModalError(null);
    try {
      // Persist final seconds_worked and clear restart_at on finish
      if (onUpdateTask) {
        await onUpdateTask(task.id, { observations: observations, restart_at: task.restart_at });
      }
      setFormErrors(prev => ({ ...prev, observations_success: 'Draft saved successfully!' }));
    } catch (error) {
      setLocalModalError(error.message || 'Error saving');
      formRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      invalidateWorkOrders();
      setLocalIsSubmitting(false);

    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLocalIsSubmitting(true);
    setLocalModalError(null);

    try {
      // Persist final seconds_worked and clear restart_at on finish
      if (onUpdateTask) {
        await onUpdateTask(task.id, { seconds_worked: elapsedSeconds, restart_at: null });
      }
      await onSave({ observations: observations.trim() });
      onClose();
    } catch (error) {
      setLocalModalError(error.message || 'Error saving');
      formRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLocalIsSubmitting(false);
    }
  };

  const handleFinishTask = async () => {
    setLocalIsSubmitting(true);
    setLocalModalError(null);
    if (!validateForm()) {
      setLocalModalError('Please fill in all required fields');
      setLocalIsSubmitting(false);
      return;
    }

    if ((task.observations || '') !== observations) {
      const confirmReplace = window.confirm("The Saved Draft and the Observations fields do not match up. Are you sure you want to save replace the current save draft with your newest input?");
      if (!confirmReplace) {
        setLocalIsSubmitting(false);
        return;
      }
    }

    try {
      const sessionSeconds = elapsedSeconds - (task.seconds_worked ?? 0);

      const otherIncompleteTasks = workOrder?.tasks?.filter(
        t => t.id !== task.id && t.assigned_to === assig?.employee_id && t.status !== 'completed'
      );



      const updateAssigPayload = {
        hours_worked: (assig?.hours_worked ?? 0) + (sessionSeconds / 3600)
      };

      if (!otherIncompleteTasks || otherIncompleteTasks.length === 0) {
        updateAssigPayload.end_time = new Date().toISOString();
      }

      if (onUpdateTask) {
        await onUpdateTask(task.id, {
          status: 'completed',
          seconds_worked: elapsedSeconds,
          restart_at: null,
          observations: observations.trim()
        });
      }

      if (assig && !assig.company_id && onUpdateAssig) {
        await onUpdateAssig(assig.id, updateAssigPayload);
      }

      if (assig && assig.company_id && onUpdateExtAssig) {
        await onUpdateExtAssig(assig.id, updateAssigPayload);
      }



      if (!isLastTask) {
        onClose();
      } else {
        setIsPaused(true);
      }


    } catch (error) {
      setLocalModalError(error.message || 'Error finishing task');
      formRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLocalIsSubmitting(false);
      invalidateWorkOrders();
    }
  };

  const handleFinishWO = async () => {
    setLocalIsSubmitting(true);
    setLocalModalError(null);
    if (completionFormData.completion_notes.trim() === '' || completionFormData.quality_check == null) {
      setLocalModalError('Please fill in all required fields');
      setLocalIsSubmitting(false);
      return;
    }

    try {
      if (isLastTask && onFinish) {

        await onFinish(workOrder, {
          completion_notes: completionFormData.completion_notes.trim(),
          quality_check: completionFormData.quality_check,
        });
      }
    } catch (error) {
      setLocalModalError(error.message || 'Error finishing WO');
    } finally {
      setLocalIsSubmitting(false);
      invalidateWorkOrders();
      onClose();
    }

  }

  useEffect(() => {
    if (localModalError) {
      formRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [localModalError]);

  const handleChange = (e) => {
    setLocalModalError('');
    const { name, value } = e.target;
    if (name === 'observations') {
      setObservations(value);
    } else {
      setCompletionFormData({
        ...completionFormData,
        [name]: value,
      });
    }
    // Clear field error when user starts typing
    if (formErrors[name] || formErrors.observations_success) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        if (newErrors.observations_success) {
          delete newErrors.observations_success;
        }
        return newErrors;
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center gap-3 justify-center p-4 bg-black/60 backdrop-blur-sm border border-white">
      <div className={`bg-gray-800 rounded-2xl border border-gray-700 shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex-1 flex flex-col`}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-white">
            {workOrder?.title || 'Work Order'} <br />
            <span className="text-xl">Task #{task?.task_number}</span>
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
            disabled={localIsSubmitting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Navigation */}
        {((!!isPaused == !!isCompleted) && !finishTrigger) && <TabNav activeTab={activeTab} setActiveTab={setActiveTab} />}


        {/* Form */}
        <form ref={formRef} onSubmit={handleSubmit} className="flex-1 overflow-y-auto flex flex-col">
          {isPaused && task.status !== 'completed' ? (
            <div className="p-6 space-y-6 flex-1">
              <div className="flex items-center gap-2 text-yellow-400 mb-4">
                <PauseCircle className="w-6 h-6" />
                <h3 className="text-xl font-semibold">Task Paused</h3>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-300">
                  Motive for pause <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={pauseMotive}
                  onChange={(e) => setPauseMotive(e.target.value)}
                  rows="6"
                  className="w-full px-4 py-3 overflow-y-auto rounded-lg border border-gray-600 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 resize-none"
                  placeholder="Please explain why the task was paused... (Required to resume)"
                />
              </div>
            </div>
          ) : finishTrigger ? (
            <div className="p-6 space-y-6 flex-1">
              {localModalError && (
                <div className="rounded-lg border border-red-800 bg-red-900/20 p-4">
                  <p className="text-red-300 text-sm">{localModalError}</p>
                </div>
              )}
              <h3 className="text-lg font-semibold text-white border-b border-gray-700 pb-2">
                Completion Notes & Quality Check
              </h3>
              <div>
                {/* Observations */}
                <div className="border-gray-700 pb-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Completion Notes <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    name="completion_notes"
                    value={completionFormData.completion_notes}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 min-h-[300px] rounded-lg border overflow-visible border-gray-600 bg-gray-700 text-white ${formErrors.completion_notes ? 'border-red-500' : 'border-gray-600'} placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y`}
                    placeholder="Completion Notes"
                    disabled={localIsSubmitting}
                  />
                  {formErrors.completion_notes && (
                    <p className="mt-1 text-sm text-red-400">{formErrors.completion_notes}</p>
                  )}
                </div>
                {/*Quality Check*/}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Quality Check <span className="text-red-400">*</span>
                  </label>
                  <div className="flex mt-2">
                    <button
                      type="button"
                      onClick={() => setCompletionFormData({ ...completionFormData, quality_check: true })}
                      className={`flex items-center justify-center gap-2 py-3 px-4 rounded-l-lg border-2 transition-all ${completionFormData.quality_check === true ? 'border-green-500 bg-green-500/20 text-green-400' : 'border-gray-600 bg-gray-700 text-gray-400 hover:bg-gray-600'}`}
                    >
                      <Check className="w-6 h-6" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setCompletionFormData({ ...completionFormData, quality_check: false })}
                      className={`flex items-center justify-center gap-2 py-3 px-4 rounded-r-lg border-2 transition-all ${completionFormData.quality_check === false ? 'border-red-500 bg-red-500/20 text-red-400' : 'border-gray-600 bg-gray-700 text-gray-400 hover:bg-gray-600'}`}
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-6 space-y-6 flex-1">
              {localModalError && (
                <div className="rounded-lg border border-red-800 bg-red-900/20 p-4">
                  <p className="text-red-300 text-sm">{localModalError}</p>
                </div>
              )}
              {/* Task Tab */}
              {activeTab === 'task' && (
                <ExecutionModalTask
                  workOrder={workOrder}
                  task={task}
                  formData={observations}
                  handleChange={handleChange}
                  formErrors={formErrors}
                  onSaveDraft={handleSaveDraft}
                  localIsSubmitting={localIsSubmitting}
                  isCompleted={isCompleted}
                  isPaused={isPaused}
                />
              )}

              {/* Spare Parts Tab */}
              {activeTab === 'spare_parts' && (
                <ExecutionModalSpareParts
                  workOrder={workOrder}
                  spareParts={spareParts}
                  queryClient={queryClient}
                  formRef={formRef}
                  isCompleted={isCompleted}
                />
              )}

              {/* Attachments Tab */}
              {activeTab === 'attachments' && (
                <ExecutionModalAttachments
                  workOrder={workOrder}
                  task={task}
                  documentTypes={documentTypes}
                  queryClient={queryClient}
                  formRef={formRef}
                  isCompleted={isCompleted}
                />
              )}
            </div>
          )}

          {/* Footer with buttons */}
          <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-end gap-3">
            <div className="text-2xl font-mono text-white">
              {formatTime(elapsedSeconds)}
            </div>
            <button
              type="button"
              onClick={togglePause}
              className="px-4 py-2 rounded-lg border border-gray-600 bg-gray-700 text-gray-300 text-sm font-medium hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              disabled={isTogglingPause || localIsSubmitting || (isPaused && !pauseMotive.trim())}
            >
              {isTogglingPause && <Loader2 className="w-4 h-4 animate-spin" />}
              {isPaused ? 'Resume' : 'Pause'}
            </button>

            {!finishTrigger &&
              <button
                type="button"
                onClick={handleFinishTask}
                className="px-4 py-2 rounded-lg border border-green-600 bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                disabled={localIsSubmitting || isPaused || isCompleted}
              >
                {localIsSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Finish Task
              </button>
            }
            {finishTrigger &&
              <button
                type="button"
                onClick={handleFinishWO}
                className="px-4 py-2 rounded-lg border border-green-600 bg-green-600 text-white text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                disabled={localIsSubmitting}
              >
                {localIsSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Finish Work Order
              </button>

            }
          </div>
        </form>
      </div>

    </div>
  );
};
